// Compact Profile Progress Widget for Dashboard
// Links to full profile page for detailed view

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { ChevronRight, Shield, CheckCircle, AlertCircle, User } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CompactProfileProgress({ userId, userRole = 'seller' }) {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchProgress();
    }
  }, [userId, userRole]);

  const fetchProgress = async () => {
    try {
      setError(null);
      // Use the correct endpoint based on role
      // The backend uses /api/profile/progress/seller/{seller_id} format
      const endpoint = userRole === 'seller' 
        ? `${API}/api/profile/progress/seller/${userId}`
        : `${API}/api/profile/progress/buyer/${userId}`;
      
      const response = await axios.get(endpoint);
      // Handle the nested response structure from backend
      if (response.data.progress) {
        setProgress(response.data.progress);
      } else {
        setProgress(response.data);
      }
    } catch (err) {
      console.error('Progress fetch error:', err);
      // Set a default progress object on error
      setProgress({ 
        overall_percentage: 0, 
        next_priority: 'Complete your profile',
        missing_items: [],
        completed_items: []
      });
      setError('Could not load progress');
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    // Navigate to profile page with progress tab
    navigate('/profile?tab=progress');
  };

  if (loading) {
    return (
      <div className="bg-slate-50 rounded-lg p-3 animate-pulse" data-testid="compact-progress-loading">
        <div className="h-4 bg-slate-200 rounded w-24 mb-2"></div>
        <div className="h-2 bg-slate-200 rounded w-full"></div>
      </div>
    );
  }

  if (!progress) return null;

  const percentage = progress.overall_percentage || 0;
  const isComplete = percentage >= 80;

  return (
    <button
      onClick={handleClick}
      className="w-full bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-150 border border-slate-200 rounded-lg p-3 transition-all text-left group"
      data-testid="compact-profile-progress"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {isComplete ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <Shield className="h-4 w-4 text-blue-600" />
          )}
          <span className="text-sm font-medium text-slate-700">
            {userRole === 'buyer' ? 'Buyer Profile' : 'Seller Profile'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className={`text-lg font-bold ${isComplete ? 'text-green-600' : 'text-blue-600'}`}>
            {percentage}%
          </span>
          <ChevronRight className="h-4 w-4 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
      <Progress value={percentage} className="h-1.5" />
      {progress.next_priority && !isComplete && (
        <p className="text-xs text-slate-500 mt-2 truncate">
          Next: {progress.next_priority}
        </p>
      )}
      {isComplete && (
        <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Profile complete!
        </p>
      )}
    </button>
  );
}
